import { supabase } from "../supabase";

// ── Tree members ──────────────────────────────────────────────

/** Fetch all members who have access to a tree (tree owner calls this). */
export async function fetchTreeMembers(treeId) {
  const { data, error } = await supabase
    .from("tree_members")
    .select("user_id, role, profiles(full_name, initials, avatar_url)")
    .eq("tree_id", treeId);
  return { members: data ?? [], error };
}

/** Grant access to a user (by userId) for a tree. */
export async function grantAccess(treeId, userId, role = "view") {
  const { data, error } = await supabase
    .from("tree_members")
    .upsert({ tree_id: treeId, user_id: userId, role }, { onConflict: "tree_id,user_id" })
    .select()
    .single();
  return { member: data, error };
}

/** Revoke a user's access to a tree. */
export async function revokeAccess(treeId, userId) {
  const { error } = await supabase
    .from("tree_members")
    .delete()
    .eq("tree_id", treeId)
    .eq("user_id", userId);
  return { error };
}

// ── Person equivalences ───────────────────────────────────────
// Conflict policy: each tree keeps its own data. The equivalence
// is a pure relational pointer — NO field sync ever happens.

/** Propose a cross-tree link: same_person or related (optionally relation_detail + relation_note). */
export async function proposeEquivalence(
  personAId,
  personBId,
  relationKind = "same_person",
  relationDetail = null,
  relationNote = null
) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { equivalence: null, error: new Error("Not authenticated") };

  const [a, b] = [personAId, personBId].sort();
  const isRelated = relationKind === "related";
  const row = {
    person_a_id: a,
    person_b_id: b,
    proposed_by: user.id,
    relation_kind: isRelated ? "related" : "same_person",
    relation_detail: isRelated ? relationDetail : null,
    relation_note: isRelated ? relationNote : null,
  };
  const { data, error } = await supabase
    .from("person_equivalences")
    .insert(row)
    .select()
    .single();
  return { equivalence: data, error };
}

/** Approve or reject an equivalence proposal. */
export async function resolveEquivalence(equivalenceId, status) {
  const { data, error } = await supabase.rpc("resolve_equivalence", {
    p_equivalence_id: equivalenceId,
    p_status: status,
  });
  return { ok: data === true, error };
}

// ── Identity claims ("C'est moi") ────────────────────────────

/** Claim that you are a specific person node. */
export async function submitIdentityClaim(personId, message = "") {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { claim: null, error: new Error("Not authenticated") };

  const { data, error } = await supabase
    .from("identity_claims")
    .insert({ claimant_id: user.id, person_id: personId, message })
    .select()
    .single();
  return { claim: data, error };
}

// ── Notifications ─────────────────────────────────────────────

/** Return the owner_id of a tree (needed to know who to notify). */
export async function fetchTreeOwner(treeId) {
  const { data, error } = await supabase
    .from("trees")
    .select("owner_id")
    .eq("id", treeId)
    .single();
  return { ownerId: data?.owner_id ?? null, error };
}

/** Insert a notification row.  The caller must know the recipient's user id.
 *  Pass treeId so the row is cascade-deleted if that tree is deleted. */
export async function createNotification(recipientId, type, payload = {}, treeId = null) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: new Error("Not authenticated") };

  const { error } = await supabase
    .from("notifications")
    .insert({ recipient_id: recipientId, type, from_user_id: user.id, payload, tree_id: treeId });
  return { error };
}

/** Fetch all notifications for the current user. */
export async function fetchNotifications() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { notifications: [], error: null };

  const { data, error } = await supabase
    .from("notifications")
    .select("*, from:from_user_id(full_name, initials)")
    .eq("recipient_id", user.id)
    .not("status", "in", '("rejected","dismissed")')
    .order("created_at", { ascending: false });
  return { notifications: data ?? [], error };
}

/** Delete all of the current user's notifications that are no longer pending. */
export async function clearResolvedNotifications() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: null };
  const { error } = await supabase
    .from("notifications")
    .delete()
    .eq("recipient_id", user.id)
    .neq("status", "pending");
  return { error };
}

/** Update a notification's status (approved / rejected / dismissed). */
export async function resolveNotification(notificationId, status) {
  const { data, error } = await supabase
    .from("notifications")
    .update({ status })
    .eq("id", notificationId)
    .select()
    .single();
  return { notification: data, error };
}

/**
 * Subscribe to real-time notification updates for the current user.
 * Returns the channel (call channel.unsubscribe() to clean up).
 *
 * Uses a unique channel topic per subscription so Supabase Realtime does not
 * return an already-joined channel for `notifications:${userId}` (which would
 * throw when adding postgres_changes listeners after subscribe).
 *
 * Requires Realtime enabled for the notifications table in Supabase Dashboard
 * (Database → Replication → supabase_realtime publication → add notifications).
 */
export function subscribeToNotifications(userId, onInsert) {
  const instanceId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  return supabase
    .channel(`notifications:${userId}:${instanceId}`)
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "notifications", filter: `recipient_id=eq.${userId}` },
      (payload) => onInsert(payload.new)
    )
    .subscribe();
}

// ── Read-only share tokens ────────────────────────────────────

/**
 * Generate a share token via Edge Function and return the full URL.
 * Requires the create-share-token Edge Function to be deployed:
 *   supabase functions deploy create-share-token
 */
export async function createShareToken(treeId, expiresInDays = 30) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { url: null, error: new Error("Not authenticated") };

  const res = await supabase.functions.invoke("create-share-token", {
    body: { treeId, expiresInDays },
  });

  if (res.error) return { url: null, error: res.error };
  return { url: res.data.url, error: null };
}

/**
 * Look up a share token and return the associated tree info.
 * Returns null if the token is expired or doesn't exist.
 */
export async function resolveShareToken(token) {
  const { data, error } = await supabase.rpc("resolve_share_token", { p_token: token });
  const row = data?.[0];

  if (error || !row) return { treeAccess: null, error };

  return {
    treeAccess: {
      id:        row.tree_id,
      name:      row.tree_name,
      ownerName: row.owner_name ?? "Inconnu",
      role:      row.role,
    },
    error: null,
  };
}

/** Accept a valid share/invite token into persistent tree membership. */
export async function acceptShareToken(token) {
  const { data, error } = await supabase.rpc("accept_share_token", { p_token: token });
  return { ok: data === true, error };
}

// ── User lookup by email ──────────────────────────────────────

/** Find a Denbaya user by their email address. Returns [] if not found. */
export async function findUserByEmail(email) {
  const { data, error } = await supabase.rpc("find_user_by_email", { p_email: email });
  return { users: data ?? [], error };
}

/** Accept an access_invitation notification — inserts caller into tree_members. */
export async function acceptAccessInvitation(notificationId) {
  const { data, error } = await supabase.rpc("accept_access_invitation", { p_notification_id: notificationId });
  return { ok: data === true, error };
}

/** Owner: accept an access_request notification — inserts the SENDER into tree_members. */
export async function acceptAccessRequest(notificationId) {
  const { data, error } = await supabase.rpc("accept_access_request", { p_notification_id: notificationId });
  return { ok: data === true, error };
}

/** Owner/admin: approve an identity claim and attach the user to the person node. */
export async function acceptIdentityClaim(notificationId) {
  const { data, error } = await supabase.rpc("accept_identity_claim", { p_notification_id: notificationId });
  return { ok: data === true, error };
}

/** Owner/admin: reject an identity claim. */
export async function rejectIdentityClaim(notificationId) {
  const { data, error } = await supabase.rpc("reject_identity_claim", { p_notification_id: notificationId });
  return { ok: data === true, error };
}

// ── Email invites ─────────────────────────────────────────────

/**
 * Send an email invite via the send-invite Edge Function.
 * Requires: supabase functions deploy send-invite
 *           supabase secrets set RESEND_API_KEY=re_xxxx
 */
export async function sendInviteEmail({ toEmail, treeId }) {
  const res = await supabase.functions.invoke("send-invite", {
    body: {
      toEmail,
      treeId,
    },
  });

  if (res.error) return { error: res.error };
  return { error: null };
}
